import React, { useState, useEffect } from 'react'
import { Search, Car, X, Plus, ChevronUp, ChevronDown, FileText, Gauge, Calendar } from 'lucide-react'
import { FormField } from './FormField'
import { useVehicles } from '../../hooks/useVehicles'
import { Input, Button } from '../ui'
import { Card, CardContent } from '../ui/Card'
import { Separator } from '../ui/separator'

import { VehicleInterest } from '../../types'

interface MultiVehicleSelectorProps {
  value?: VehicleInterest[]
  onChange: (vehicleInterests: VehicleInterest[]) => void
  error?: string
  required?: boolean
  onCreateProposal?: (vehicleId: string) => void
  leadId?: string
}

export const MultiVehicleSelector: React.FC<MultiVehicleSelectorProps> = ({
  value = [],
  onChange,
  error,
  required = false,
  onCreateProposal,
  leadId
}) => {
  const { vehicles, isLoading, fetchVehicles } = useVehicles()
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const hasLoadedRef = React.useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      fetchVehicles({ limit: 100, status: 'in_stock' })
      hasLoadedRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleSearch = (term: string) => {
    setSearchTerm(term)
    if (term.length >= 3) {
      fetchVehicles({ search: term, limit: 100, status: 'in_stock', category: categoryFilter || undefined })
      return
    }
    if (term.length === 0) {
      fetchVehicles({ limit: 100, status: 'in_stock', category: categoryFilter || undefined })
    }
  }

  const handleCategoryFilter = (category: string) => {
    setCategoryFilter(category)
    fetchVehicles({
      limit: 100,
      status: 'in_stock',
      category: category || undefined,
      ...(searchTerm.length >= 3 ? { search: searchTerm } : {}),
    })
  }

  const handleAdd = (vehicle: any) => {
    if (!value.find(v => v.vehicleId === vehicle.id)) {
      const newInterests: VehicleInterest[] = [
        ...value,
        {
          vehicleId: vehicle.id,
          priority: value.length + 1,
          notes: ''
        }
      ]
      onChange(newInterests)
    }
    setSearchTerm('')
    setIsOpen(false)
  }

  const handleRemove = (vehicleId: string) => {
    const newInterests = value
      .filter(v => v.vehicleId !== vehicleId)
      .map((v, index) => ({ ...v, priority: index + 1 }))
    onChange(newInterests)
  }

  const handleNotesChange = (vehicleId: string, notes: string) => {
    const newInterests = value.map(v =>
      v.vehicleId === vehicleId ? { ...v, notes } : v
    )
    onChange(newInterests)
  }

  const handlePriorityUp = (index: number) => {
    if (index === 0) return
    const newInterests = [...value]
    const temp = newInterests[index]
    newInterests[index] = newInterests[index - 1]
    newInterests[index - 1] = temp
    newInterests.forEach((v, i) => v.priority = i + 1)
    onChange(newInterests)
  }

  const handlePriorityDown = (index: number) => {
    if (index === value.length - 1) return
    const newInterests = [...value]
    const temp = newInterests[index]
    newInterests[index] = newInterests[index + 1]
    newInterests[index + 1] = temp
    newInterests.forEach((v, i) => v.priority = i + 1)
    onChange(newInterests)
  }

  const getVehicleName = (vehicle: any) => {
    if (vehicle.name) return vehicle.name

    const brand = vehicle.brand?.name || vehicle.brandName || ''
    const model = vehicle.model?.name || vehicle.modelName || ''
    const version = vehicle.version?.name || vehicle.catalog?.version?.name || ''
    const year = vehicle.modelYear || vehicle.catalog?.modelYear || ''

    return `${brand} ${model} ${version} ${year}`.replace(/\s+/g, ' ').trim()
  }

  const formatPrice = (price: string | number | undefined) => {
    if (price === undefined || price === null) return 'R$ 0,00'
    const numPrice = typeof price === 'string' ? parseFloat(price) : price
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numPrice)
  }

  const filteredVehicles = vehicles
    .filter(vehicle => {
      if (!searchTerm) return true
      const vehicleName = getVehicleName(vehicle).toLowerCase()
      return vehicleName.includes(searchTerm.toLowerCase())
    })
    .filter(vehicle => !value.find(v => v.vehicleId === vehicle.id))

  const getVehicleById = (vehicleId: string) => {
    return vehicles.find(v => v.id === vehicleId)
  }

  return (
    <FormField label="Veículos de Interesse" error={error} required={required}>
      <div className="space-y-3">
        {/* Veículos Selecionados */}
        {value.length > 0 && (
          <div className="space-y-2">
            {value.map((interest, index) => {
              const vehicle = getVehicleById(interest.vehicleId)
              if (!vehicle) return null

              const vehiclePhoto =
                vehicle.photos?.[0]?.url ||
                vehicle.photos?.[0]?.thumbnail ||
                (vehicle.images && vehicle.images.length > 0 ? vehicle.images[0] : null)

              const vehiclePrice = vehicle.salePrice || vehicle.price

              return (
                <Card key={interest.vehicleId} className="overflow-hidden">
                  <CardContent className="p-0">
                    <div className="flex">
                      {/* Foto do Veículo */}
                      {vehiclePhoto && (
                        <div className="w-28 h-28 flex-shrink-0 bg-surface">
                          <img
                            src={vehiclePhoto}
                            alt={getVehicleName(vehicle)}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}

                      {/* Conteúdo do Card */}
                      <div className="flex-1 p-3 space-y-2">
                        {/* Header com título e ações */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-accent text-white text-[10px] font-bold flex-shrink-0">
                                {interest.priority}
                              </span>
                              <h4 className="font-semibold text-sm text-foreground leading-tight">
                                {getVehicleName(vehicle)}
                              </h4>
                            </div>

                            {/* Informações do Veículo */}
                            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                              {vehicle.mileage != null && (
                                <span className="flex items-center gap-1">
                                  <Gauge className="h-3 w-3" />
                                  {vehicle.mileage.toLocaleString('pt-BR')} km
                                </span>
                              )}
                              {vehicle.modelYear && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {vehicle.modelYear}
                                </span>
                              )}
                              {vehicle.specifications?.body?.color && <span>{vehicle.specifications?.body?.color}</span>}
                            </div>

                            {/* Preço */}
                            {vehiclePrice && (
                              <div className="text-sm font-semibold text-accent font-mono">
                                {formatPrice(vehiclePrice)}
                              </div>
                            )}
                          </div>

                          {/* Controles de Ação */}
                          <div className="flex gap-0.5">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handlePriorityUp(index)}
                              disabled={index === 0}
                              className="h-7 w-7"
                              title="Aumentar prioridade"
                            >
                              <ChevronUp className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handlePriorityDown(index)}
                              disabled={index === value.length - 1}
                              className="h-7 w-7"
                              title="Diminuir prioridade"
                            >
                              <ChevronDown className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemove(interest.vehicleId)}
                              className="h-7 w-7 text-destructive hover:text-destructive"
                              title="Remover veículo"
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>

                        <Separator />

                        {/* Campo de Observações */}
                        <Input
                          value={interest.notes}
                          onChange={(e) => handleNotesChange(interest.vehicleId, e.target.value)}
                          placeholder="Observações sobre este veículo..."
                          className="text-xs h-8"
                        />

                        {/* Botão Fazer Proposta */}
                        {onCreateProposal && leadId && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => onCreateProposal(interest.vehicleId)}
                            className="w-full h-8 text-xs"
                          >
                            <FileText className="h-3.5 w-3.5 mr-1.5" />
                            Criar Proposta
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}

        {/* Dropdown de Busca */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={`w-full flex items-center gap-2 px-3 py-2 border rounded-md bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent text-sm transition-colors ${
              error ? 'border-red-500' : 'border-border hover:border-accent/40'
            }`}
          >
            <Plus className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="text-muted-foreground">
              {value.length > 0 ? 'Adicionar mais veículos' : 'Selecione os veículos de interesse'}
            </span>
          </button>

          {isOpen && (
            <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-80 overflow-hidden">
              {/* Search Input */}
              <div className="p-2 border-b border-border">
                <div className="mb-2">
                  <select
                    value={categoryFilter}
                    onChange={(e) => handleCategoryFilter(e.target.value)}
                    className="w-full px-2 py-1.5 border border-border rounded-md bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    <option value="">Todas as categorias</option>
                    <option value="A">A (Motos)</option>
                    <option value="B">B (Carros)</option>
                    <option value="C">C (Caminhões)</option>
                    <option value="D">D (Ônibus)</option>
                  </select>
                </div>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Buscar por marca, modelo, versão..."
                    className="w-full pl-8 pr-3 py-1.5 border border-border rounded-md bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                    autoFocus
                  />
                </div>
              </div>

              {/* Vehicle List */}
              <div className="overflow-y-auto max-h-60">
                {isLoading ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    Carregando veículos...
                  </div>
                ) : filteredVehicles.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    {searchTerm ? 'Nenhum veículo encontrado' : 'Todos os veículos já foram adicionados ou nenhum disponível'}
                  </div>
                ) : (
                  filteredVehicles.map((vehicle) => (
                    <button
                      key={vehicle.id}
                      type="button"
                      onClick={() => handleAdd(vehicle)}
                      className="w-full text-left px-3 py-2.5 hover:bg-surface transition-colors border-b border-border last:border-b-0"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <Car className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium text-sm text-foreground truncate">
                              {getVehicleName(vehicle)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {vehicle.category && `${vehicle.category}`}
                              {vehicle.specifications?.body?.color && `${vehicle.specifications?.body?.color}`}
                              {vehicle.mileage != null && ` • ${vehicle.mileage.toLocaleString('pt-BR')} km`}
                            </div>
                          </div>
                        </div>
                        {(vehicle.salePrice || vehicle.price) && (
                          <div className="text-sm font-semibold text-accent font-mono flex-shrink-0">
                            {formatPrice(vehicle.salePrice || vehicle.price)}
                          </div>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </FormField>
  )
}
